import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import CheckCircleIcon from "@medoma/components/icons/CheckCircleIcon";
import CircleIcon from "@medoma/components/icons/CircleIcon";
import { PlainButton } from "@medoma/components/Button/Button";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Loading } from "@medoma/components/Loading/Loading";
import { Popover } from "@/components/Popover/Popover";
import { updatePostDischargeProcessed } from "@/api/Patients";
import { deducedError } from "@/Utils/ErrorUtils";
import styles from "./PostDischargeProcessedCell.module.scss";

type PostDischargeProcessedCellProps = {
  patientId: string;
  postDischargeProcessed: boolean | null | undefined;
};

export const PostDischargeProcessedCell = ({
  patientId,
  postDischargeProcessed,
}: PostDischargeProcessedCellProps) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();

  const { mutate, isPending, isError, error } = useMutation({
    mutationFn: () => updatePostDischargeProcessed(patientId, true),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  if (postDischargeProcessed) {
    return (
      <div
        className={styles.processedIndicator}
        aria-label={t`Vårdperioden är efterhanterad`}
      >
        <CheckCircleIcon />
      </div>
    );
  }

  return (
    <Popover
      trigger={{
        custom: (
          <div className={styles.unprocessedIndicator}>
            <CircleIcon />
          </div>
        ),
        ariaLabel: t`Visa information om efterhantering`,
      }}
    >
      <div className={styles.content}>
        {isPending ? <Loading message={t`Markerar som efterhanterad`} /> : null}
        {isError ? (
          <ErrorMessage
            message={`${t`Det gick inte att markera vårdperioden som efterhanterad.`} ${deducedError(error)}`}
          />
        ) : null}
        <p>
          <Trans>Vårdperioden är inte efterhanterad</Trans>
        </p>
        <PlainButton disabled={isPending} onClick={() => mutate()}>
          <Trans>Markera som efterhanterad</Trans>
        </PlainButton>
      </div>
    </Popover>
  );
};
